import { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import * as openpgp from "openpgp";
import { Tooltip } from "@mui/material";

import { getUserId } from "utils/getUserId";
import { getKeys } from "utils/getKeys";
import ModalsManagerContext from "contexts/ModalsManager";
import CopyIcon from "assets/icons/copy.svg?react";
import CloseIcon from "assets/icons/close.svg?react";

import styles from "./styles.module.scss";

// Modalne, centralne okno z kodem przywracania. Logika odszyfrowania klucza
// powielona z komponentu EncryptionKey w SecurityTab; wygląd wg projektu Figma
// (Popup_kod QR).
const RecoveryCodeModal = () => {
  const { t } = useTranslation();
  const { modals, setModals } = useContext(ModalsManagerContext);
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [key, setKey] = useState<string>(null);

  const loadKey = async () => {
    const recovKey = localStorage.getItem(`${userId}.recov`);

    if (recovKey?.length) {
      const userKeys = await getKeys(userId);

      const intermediateKey = await openpgp.key.readArmored(
        atob(userKeys!.intermidatePrivateKey!.data),
      );

      const options: openpgp.DecryptOptions = {
        message: await openpgp.message.readArmored(atob(recovKey)),
        privateKeys: intermediateKey.keys,
      };

      openpgp.decrypt(options).then((r) => {
        setKey(r.data as string);
      });
    }
  };

  useEffect(() => {
    loadKey();
  }, []);

  const close = () => setModals({ ...modals, recoveryCode: false });

  const qrData =
    import.meta.env.VITE_APP_SITE + `/recoverqr/${userId}#${key}`;

  return ReactDOM.createPortal(
    <div className={styles.modal} onClick={close}>
      <div className={styles.wrapper} onClick={(e) => e.stopPropagation()}>
        <div className={styles.closeBtn} onClick={close}>
          <CloseIcon />
        </div>

        <div className={styles.header}>{t("sidebar.recoveryCode")}</div>
        <div className={styles.hint}>{t("settings.security.privateKeyHint")}</div>

        {!!key?.length && (
          <div className={styles.qrCodeWrapper}>
            <QRCodeCanvas value={qrData} size={232} />
          </div>
        )}

        {!!key?.length && (
          <div className={styles.keyBox}>
            <div className={styles.key}>{key}</div>
            <div
              className={styles.copy}
              onClick={() => navigator.clipboard.writeText(key)}
            >
              <Tooltip title={t("common.copy")}>
                <div className={styles.copyInner}>
                  <CopyIcon></CopyIcon>
                </div>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RecoveryCodeModal;
